import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { login, verifyTwoFactorLogin } from "../api_controll/apiClient";
import { setUser } from "../redux/userSlice";
import "../styles/auth-password.css";

function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorExpiresIn, setTwoFactorExpiresIn] = useState(null);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);

  useEffect(() => {
    if (user?.username) {
      navigate("/stations", { replace: true });
    }
  }, [user, navigate]);

  const finishLogin = (data) => {
    if (!data?.user?.id || !data?.user?.username || !data?.user?.role) {
      throw new Error("Invalid login response");
    }

    dispatch(
      setUser({
        id: data.user.id,
        username: data.user.username,
        role_name: data.user.role,
      })
    );

    navigate("/stations", { replace: true });
  };

  const handleLogin = async (e) => {
  e.preventDefault();
  setError(null);

  const cleanUsername = username.trim();

  if (!cleanUsername || !password) {
    setError("Vyplňte uživatelské jméno a heslo.");
    return;
  }

  try {
    setLoading(true);

    const data = await login({
      username: cleanUsername,
      password,
    });

    if (data?.requires_2fa) {
      setTwoFactorRequired(true);
      setChallengeToken(data.challenge_token || "");
      setTwoFactorExpiresIn(data.expires_in || null);
      setTwoFactorCode("");
      setPassword("");
      return;
    }

    finishLogin(data);
  } catch (err) {
    console.error("Login failed:", err);
    setError(getLoginErrorMessage(err));
  } finally {
    setLoading(false);
  }
};

  const handleVerifyTwoFactor = async (e) => {
  e.preventDefault();
  setError(null);

  const cleanCode = twoFactorCode.trim();

  if (!challengeToken) {
    setError("Chybí ověřovací token. Zkuste se přihlásit znovu.");
    return;
  }

  if (!/^\d{6}$/.test(cleanCode)) {
    setError("Zadejte 6místný ověřovací kód.");
    return;
  }

  try {
    setLoading(true);

    const data = await verifyTwoFactorLogin({
      challenge_token: challengeToken,
      code: cleanCode,
    });

    finishLogin(data);
  } catch (err) {
    console.error("2FA verification failed:", err);
    setError(getTwoFactorErrorMessage(err));
  } finally {
    setLoading(false);
  }
};

  const getLoginErrorMessage = (err) => {
  const message = err?.message || "";

  switch (message) {
    case "Verification code was sent recently":
      return "Ověřovací kód jsme už nedávno odeslali. Počkejte prosím chvíli a zkuste to znovu.";

    case "User email missing or invalid":
      return "Uživatel nemá nastavený platný email. Kontaktujte správce systému.";

    case "Invalid credentials":
      return "Neplatné uživatelské jméno nebo heslo.";

    case "User is disabled":
      return "Uživatelský účet je deaktivovaný. Kontaktujte správce systému.";

    case "Two-factor verification failed":
      return "Ověření přihlášení se nezdařilo. Zkuste to prosím znovu.";

    default:
      return "Přihlášení se nezdařilo. Zkontrolujte údaje a zkuste to znovu.";
  }
};

const getTwoFactorErrorMessage = (err) => {
  const message = err?.message || "";

  switch (message) {
    case "Invalid verification code":
      return "Zadaný ověřovací kód není správný.";

    case "Invalid or expired verification challenge":
      return "Ověřovací kód je neplatný nebo expirovaný. Přihlaste se prosím znovu.";

    case "Too many verification attempts":
      return "Bylo zadáno příliš mnoho neplatných kódů. Přihlaste se prosím znovu a nechte si poslat nový kód.";

    default:
      return "Ověřovací kód je neplatný nebo expirovaný.";
  }
};

  const handleBackToLogin = () => {
    setTwoFactorRequired(false);
    setChallengeToken("");
    setTwoFactorCode("");
    setTwoFactorExpiresIn(null);
    setPassword("");
    setError(null);
  };

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--login">
        <div className="auth-card__brand">
          <div>
            <div className="auth-card__appName">Artis</div>
            <div className="auth-card__appSub">Správa cen konkurence</div>
          </div>
        </div>

        {!twoFactorRequired ? (
          <>
            <div className="auth-card__header">
              <h2>Přihlášení</h2>
              <p>Přihlaste se svým uživatelským jménem a heslem.</p>
            </div>

            {error && <div className="auth-card__error">{error}</div>}

            <form onSubmit={handleLogin} className="auth-card__form">
              <div className="auth-card__field">
                <label htmlFor="username">Uživatelské jméno</label>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  placeholder="např. cs_decin"
                  value={username}
                  disabled={loading}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div className="auth-card__field">
                <label htmlFor="password">Heslo</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Zadejte heslo"
                  value={password}
                  disabled={loading}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div className="auth-card__row">
                <Link className="auth-card__link" to="/forgot-password">
                  Zapomenuté heslo?
                </Link>
              </div>

              <button type="submit" disabled={loading}>
                {loading ? "Přihlašuji…" : "Přihlásit"}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="auth-card__header">
              <h2>Ověření přihlášení</h2>
              <p>
                Na email uživatele jsme poslali ověřovací kód. Zadejte ho pro
                dokončení přihlášení.
              </p>
            </div>

            <div className="auth-card__password-rules">
              Kód je 6místný a platí přibližně{" "}
              {twoFactorExpiresIn ? Math.ceil(twoFactorExpiresIn / 60) : 5} minut.
            </div>

            {error && <div className="auth-card__error">{error}</div>}

            <form onSubmit={handleVerifyTwoFactor} className="auth-card__form">
              <div className="auth-card__field">
                <label htmlFor="two-factor-code">Ověřovací kód</label>
                <input
                  id="two-factor-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={twoFactorCode}
                  disabled={loading}
                  maxLength={6}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setTwoFactorCode(value.slice(0, 6));
                  }}
                />
              </div>

              <button type="submit" disabled={loading}>
                {loading ? "Ověřuji…" : "Dokončit přihlášení"}
              </button>

              <button
                type="button"
                className="auth-card__secondaryButton"
                onClick={handleBackToLogin}
                disabled={loading}
              >
                Zpět na přihlášení
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default LoginPage;